/**
 * Classes d'équivalence (preneur) — miroir backend card-equivalence.ts.
 * Indices 0–77 (cardId - 1).
 */

const EXCLUDED_IDX = new Set([56, 76, 77]);

function cardPoints(cardIdx: number): number {
  const cardId = cardIdx + 1;
  if (cardId === 78) return 4.5;
  if (cardId >= 57) return EXCLUDED_IDX.has(cardIdx) ? 4.5 : 0.5;
  const rank = ((cardId - 1) % 14) + 1;
  return ({ 11: 1.5, 12: 2.5, 13: 3.5, 14: 4.5 } as Record<number, number>)[rank] ?? 0.5;
}

function find(parent: Map<number, number>, x: number): number {
  let root = x;
  while (parent.get(root) !== root) root = parent.get(root)!;
  let cur = x;
  while (parent.get(cur) !== root) {
    const next = parent.get(cur)!;
    parent.set(cur, root);
    cur = next;
  }
  return root;
}

function union(parent: Map<number, number>, a: number, b: number): void {
  const ra = find(parent, a);
  const rb = find(parent, b);
  if (ra !== rb) parent.set(ra, rb);
}

export interface EquivalenceOptions {
  role?: 'attacker' | string;
  lowRankLimit?: number;
  valetCavalierEquiv?: boolean;
  enableTypeB?: boolean;
}

export function equivalenceClasses(
  hand: number[],
  options: EquivalenceOptions = {},
): Set<number>[] {
  const {
    role = 'attacker',
    lowRankLimit: rawLimit = 5,
    valetCavalierEquiv = false,
    enableTypeB = true,
  } = options;

  if (role !== 'attacker') return hand.map(c => new Set([c]));

  const lowRankLimit = Math.max(5, Math.min(10, Math.floor(rawLimit)));
  const handSet = new Set(hand);
  const parent = new Map<number, number>();
  for (const c of hand) parent.set(c, c);

  for (let suit = 0; suit < 4; suit++) {
    const low: number[] = [];
    for (let r = 1; r <= lowRankLimit; r++) {
      const idx = suit * 14 + r - 1;
      if (handSet.has(idx) && !EXCLUDED_IDX.has(idx)) low.push(idx);
    }
    for (let i = 1; i < low.length; i++) union(parent, low[0]!, low[i]!);
  }

  if (enableTypeB) {
    for (let suit = 0; suit < 4; suit++) {
      for (let r = 1; r < 14; r++) {
        const idxA = suit * 14 + r - 1;
        const idxB = suit * 14 + r;
        if (handSet.has(idxA) && handSet.has(idxB)
          && !EXCLUDED_IDX.has(idxA) && !EXCLUDED_IDX.has(idxB)
          && cardPoints(idxA) === cardPoints(idxB)) {
          union(parent, idxA, idxB);
        }
      }
    }
    for (let r = 2; r < 21; r++) {
      const idxA = 55 + r;
      const idxB = 55 + r + 1;
      if (handSet.has(idxA) && handSet.has(idxB)
        && !EXCLUDED_IDX.has(idxA) && !EXCLUDED_IDX.has(idxB)
        && cardPoints(idxA) === cardPoints(idxB)) {
        union(parent, idxA, idxB);
      }
    }
  }

  if (valetCavalierEquiv) {
    for (let suit = 0; suit < 4; suit++) {
      const valetIdx = suit * 14 + 10;
      const cavIdx = suit * 14 + 11;
      if (handSet.has(valetIdx) && handSet.has(cavIdx)) union(parent, valetIdx, cavIdx);
    }
  }

  const rootToClass = new Map<number, Set<number>>();
  for (const c of hand) {
    const root = find(parent, c);
    if (!rootToClass.has(root)) rootToClass.set(root, new Set());
    rootToClass.get(root)!.add(c);
  }
  return Array.from(rootToClass.values());
}

export function equivalenceClassKey(
  cardIdx: number,
  hand: number[],
  options: EquivalenceOptions = {},
): string {
  for (const cls of equivalenceClasses(hand, options)) {
    if (cls.has(cardIdx)) return `eq:${Math.min(...cls)}`;
  }
  return `eq:${cardIdx}`;
}
